import { CheckoutParamEnum, PaymentTypeEnum } from 'components/views/pages/Checkout/types';
import { usePropertyInformationQuery } from 'components/views/pages/Checkout/hooks/usePropertyInformationQuery';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useCheckoutParams } from './useCheckoutParams';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayjs from 'dayjs';
import { dateFormatServer, handleAddDay } from '@alluradirect/react';

dayjs.extend(customParseFormat);
interface Param {
  name: CheckoutParamEnum;
  value: string | number;
}

interface Args {
  isFirstStep: boolean;
}

export const useCheckoutFixInvalidParams = ({ isFirstStep }: Args) => {
  const router = useRouter();

  const { step, adults, pets, children, infants, startDate, endDate, paymentType, marketing, propertyId } = useCheckoutParams();

  const propertyInfo = usePropertyInformationQuery({ pid: Number(propertyId), enabled: Boolean(propertyId) });
  const balanceDueDays = propertyInfo.data?.DATA?.BALANCE_DUE_DAYS ?? 45;
  const maxBookableDays = propertyInfo.data?.DATA?.MAX_BOOKABLE_DAYS
    ? Number(propertyInfo.data.DATA.MAX_BOOKABLE_DAYS)
    : undefined;

  useEffect(() => {
    if (!router.isReady || !step) return;

    const paramsToFix: Param[] = [];

    // to support no guests by default
    if (adults !== undefined || children !== undefined || infants !== undefined) {
      if (!adults || (adults && Number(adults) <= 0)) {
        paramsToFix.push({
          name: CheckoutParamEnum.adults,
          value: 1,
        });
      }
  
      if (!children || (children && Number(children) < 0)) {
        paramsToFix.push({
          name: CheckoutParamEnum.children,
          value: 0,
        });
      }
  
      if (!infants || (infants && Number(infants) < 0)) {
        paramsToFix.push({
          name: CheckoutParamEnum.infants,
          value: 0,
        });
      }
    }

    if (!pets || (pets && Number(pets) < 0)) {
      paramsToFix.push({
        name: CheckoutParamEnum.pets,
        value: 0,
      });
    }

    if (!marketing || (marketing && Number(marketing) < 0) || (marketing && Number(marketing) > 1)) {
      paramsToFix.push({
        name: CheckoutParamEnum.marketing,
        value: 1,
      });
    }

    const noDatesAndNotFirstStep = !isFirstStep && (!startDate || !endDate);
    const isStartDateValid = dayjs(startDate, 'YYYY-MM-DD', true).isValid();
    const isEndDateValid = dayjs(endDate, 'YYYY-MM-DD', true).isValid();
    const today = dateFormatServer(dayjs());

    if (startDate && !isStartDateValid) {
      paramsToFix.push({
        name: CheckoutParamEnum.startDate,
        value: today,
      });
    }

    if (startDate && endDate && !isEndDateValid && isStartDateValid) {
      const nextValidEndDate = dateFormatServer(handleAddDay(startDate ?? today));

      paramsToFix.push({
        name: CheckoutParamEnum.endDate,
        value: nextValidEndDate,
      });
    } else if (startDate && endDate && isStartDateValid && isEndDateValid) {
      const start = dayjs(startDate, 'YYYY-MM-DD', true);
      const end = dayjs(endDate, 'YYYY-MM-DD', true);
      const nights = end.diff(start, 'day');

      if (maxBookableDays !== undefined && maxBookableDays > 0 && nights > maxBookableDays) {
        const adjustedEndDate = dateFormatServer(start.add(maxBookableDays, 'day'));

        paramsToFix.push({
          name: CheckoutParamEnum.endDate,
          value: adjustedEndDate,
        });
      }
    }

    if (noDatesAndNotFirstStep) {
      paramsToFix.push({
        name: CheckoutParamEnum.step,
        value: 1,
      });
    }

    if (!paymentType) {
      let paymentType_id = 0;
      if( isStartDateValid ){
        paymentType_id = PaymentTypeEnum.SinglePayment;
        if( Math.abs(dayjs().diff(dayjs(startDate, 'YYYY-MM-DD', true), 'day')) >= balanceDueDays ){
          paymentType_id = PaymentTypeEnum.DepositAndBalancePayment;
        } else {
          paymentType_id = PaymentTypeEnum.SinglePayment;
        }
      } 
      if ( (paymentType && Number(paymentType) > 2) || Number(paymentType) <= 0) {
        paymentType_id = PaymentTypeEnum.SinglePayment;
      }
      if( paymentType_id > 0 ){
        paramsToFix.push({
          name: CheckoutParamEnum.paymentType,
          value: paymentType_id,
        });
      }
    } else if( Number(paymentType) == PaymentTypeEnum.DepositAndBalancePayment && isStartDateValid && Math.abs(dayjs().diff(dayjs(startDate, 'YYYY-MM-DD', true), 'day')) < balanceDueDays ){
      paramsToFix.push({
        name: CheckoutParamEnum.paymentType,
        value: PaymentTypeEnum.SinglePayment,
      });

    }


    if (paramsToFix.length) {
      router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          ...paramsToFix.reduce((curr, next) => {
            const val = {
              ...curr,
              [next.name]: next.value,
            };
            return val;
          }, {}),
        },
      });
    }
  }, [adults, endDate, isFirstStep, children, paymentType, pets, router, startDate, step, infants, marketing, balanceDueDays, maxBookableDays]);
};
